import React, { useEffect, useRef } from 'react';
import './Drawer.css';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Standard right-side drawer used for create/edit forms across the app.
 *
 * Owns the shell only — overlay, slide-in panel, header with close button,
 * scrollable body and pinned footer — plus Escape-to-close, a focus trap
 * and body scroll lock. Pass the CTAs as `footer`, using the
 * `app-drawer-btn` classes so every drawer's buttons look the same.
 * `title` may be a node; `headerExtra` renders under the header (e.g. tabs);
 * `ariaLabel` names the dialog when `title` isn't plain text.
 */
const Drawer = ({ isOpen, onClose, title, subtitle, headerExtra, footer, ariaLabel, className = '', children }) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusable = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="app-drawer-overlay" onClick={onClose}>
      <aside
        className={`app-drawer ${className}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || (typeof title === 'string' ? title : undefined)}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="app-drawer-header">
          <div>
            <h2 className="app-drawer-title">{title}</h2>
            {subtitle && <p className="app-drawer-subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="app-drawer-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {headerExtra}

        <div className="app-drawer-body">{children}</div>

        {footer && <div className="app-drawer-footer">{footer}</div>}
      </aside>
    </div>
  );
};

export default Drawer;
